import type { PlanningInsightItemDto } from "../../../api/planningInsights";
import { formatDisplayDate, formatDisplayDateValue } from "../../../utils/dateDisplay";
import { ClientTable, Tabs } from "../../shared";
import {
  BulletList,
  ConfidenceBadge,
  DetailAccordionGroup,
  DetailAccordionSection,
  DetailKeyMessage,
  DetailMetadataStrip,
  DetailModalShell,
  DetailStatusBadge,
  DetailSubsection,
  LinkedItems,
  PriorityBadge,
  StatusBadge,
} from "../AiInsightsRaidDetailParts";
import { getInsightEvidenceRoute } from "../evidenceNavigation";
import type { PlanningAttentionItemDto, PlanningEvidenceItemDto } from "./AiInsightsPlanningTypes";

export type AiInsightsPlanningDetailModalProps = {
  item: PlanningAttentionItemDto;
  sourceItem?: PlanningInsightItemDto;
  onClose: () => void;
};

function display(value?: string | number | boolean | null) {
  if (value == null || value === "") return "Not available";
  return typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
}

function displayDate(value?: string | null) {
  if (value == null || value === "") return "Not available";
  return formatDisplayDate(value, { fallback: "Not available" });
}

function formatEvidenceTimestamp(value: string) {
  return formatDisplayDateValue(value);
}

function formatAssessment(value?: string | null) {
  const normalized = value?.trim().toLowerCase().replace(/[_-]+/g, " ");
  if (normalized === "at risk") return "At risk";
  if (normalized === "red") return "At risk (Red)";
  if (normalized === "amber" || normalized === "yellow") return `Needs attention (${value})`;
  if (normalized === "green") return "On track (Green)";
  return value;
}

function PlanningEvidenceList({ items }: { items?: PlanningEvidenceItemDto[] | null }) {
  if (items == null || items.length === 0) {
    return <p className="global-insights-overlay__risk-detail-text">Not available</p>;
  }

  return (
    <div className="global-insights-overlay__planning-evidence-list">
      {items.map((evidence, index) => {
        const id = evidence.source_id?.trim();
        const route = id ? getInsightEvidenceRoute(id, evidence.source_type) : undefined;
        const content = (
          <>
            <span className="global-insights-overlay__planning-evidence-id">
              ID: {id || "Not available"}
            </span>
            <span className="global-insights-overlay__planning-evidence-summary">
              {evidence.evidence_summary?.trim() || "Not available"}
            </span>
            {evidence.source_timestamp && (
              <span className="global-insights-overlay__planning-evidence-timestamp">
                {formatEvidenceTimestamp(evidence.source_timestamp)}
              </span>
            )}
          </>
        );

        return route != null ? (
          <a
            key={`${id}-${index}`}
            className="global-insights-overlay__planning-evidence-card global-insights-overlay__planning-evidence-card--clickable"
            href={route}
            aria-label={`Open evidence ${id}`}
          >
            {content}
          </a>
        ) : (
          <div key={`${id ?? "evidence"}-${index}`} className="global-insights-overlay__planning-evidence-card">
            {content}
          </div>
        );
      })}
    </div>
  );
}

export function AiInsightsPlanningDetailModal({
  item,
  sourceItem,
  onClose,
}: AiInsightsPlanningDetailModalProps) {
  const planningDetailsActivityId = sourceItem?.stableItemId ?? null;
  const assessment = formatAssessment(item.carter_assessment);
  const overviewRow = {
    id: sourceItem?.planningItemRef ?? item.planning_anchor?.anchor_id ?? item.item_id,
    itemType: item.item_type,
    workstream: sourceItem?.workstream ?? item.workstream,
    owner: sourceItem?.owner ?? item.owner,
    status: sourceItem?.status ?? item.source_status,
    priority: formatAssessment(sourceItem?.priority ?? item.carter_assessment),
    day1Critical: sourceItem?.isDay1Critical,
  };
  const scheduleRows = [
    {
      baselineStart: sourceItem?.plannedStartDate ?? item.dates?.baseline_start_date,
      baselineEnd: sourceItem?.plannedEndDate ?? item.dates?.baseline_end_date,
      forecastStart: item.dates?.forecast_start_date,
      forecastEnd: item.dates?.forecast_end_date,
      actualStart: sourceItem?.actualStartDate ?? item.dates?.actual_start_date,
      actualEnd: sourceItem?.actualEndDate ?? item.dates?.actual_end_date,
    },
  ];
  const attentionDrivers = Array.isArray(item.attention_drivers) ? item.attention_drivers : [];
  const supportingItems = Array.isArray(item.supporting_linked_items)
    ? item.supporting_linked_items
    : [];
  const sourceContext = [
    sourceItem?.description,
    sourceItem?.dependencyText && `Dependency: ${sourceItem.dependencyText}`,
    sourceItem?.riskOfDelay && `Recorded risk of delay: ${sourceItem.riskOfDelay}`,
    sourceItem?.impactOfDelay && `Recorded impact of delay: ${sourceItem.impactOfDelay}`,
  ].filter((value): value is string => value != null && value.trim().length > 0);

  return (
    <DetailModalShell
      title={sourceItem?.title ?? item.item_name ?? "Planning insight detail"}
      onClose={onClose}
      className="global-insights-overlay__risk-detail-modal--wide"
      lastUpdatedAt={sourceItem?.lastUpdatedAt}
    >
      <DetailMetadataStrip
        items={[
          {
            label: "Planning ID",
            value: display(overviewRow.id),
            provenance: sourceItem?.planningItemRef != null ? "Source record" : "AI assessed",
          },
          { label: "Type", value: display(overviewRow.itemType), provenance: "AI assessed" },
          {
            label: "Workstream",
            value: display(overviewRow.workstream),
            provenance: sourceItem?.workstream != null ? "Source record" : "AI assessed",
          },
          {
            label: "Owner",
            value: display(overviewRow.owner),
            provenance: sourceItem?.owner != null ? "Source record" : "AI assessed",
          },
          {
            label: "Status",
            value: <StatusBadge value={overviewRow.status} />,
            provenance: sourceItem?.status != null ? "Source record" : undefined,
          },
          {
            label: "Assessment",
            value: <PriorityBadge value={overviewRow.priority} />,
            provenance:
              item.carter_assessment != null
                ? "AI assessed"
                : sourceItem?.priority != null
                  ? "Source record"
                  : undefined,
          },
          {
            label: "Day 1 critical",
            value: display(overviewRow.day1Critical),
            provenance: "Source record",
          },
          {
            label: "Confidence",
            value: (
              <span className="global-insights-overlay__ai-source-value">
                <ConfidenceBadge value={item.confidence} />
              </span>
            ),
            provenance: "AI assessed",
          },
        ]}
      />
      {sourceItem == null && (
        <p className="global-insights-overlay__risk-detail-text">
          Source planning record unavailable.
        </p>
      )}
      {planningDetailsActivityId != null && (
        <a
          className="global-insights-overlay__planning-record-link"
          href={`/integration-planning?focusId=${encodeURIComponent(sourceItem?.planningItemRef ?? planningDetailsActivityId)}&tab=detailed-view`}
        >
          Open planning record
        </a>
      )}

      <DetailKeyMessage label="Why attention is required now">
        {display(item.why_attention_is_required_now)}
      </DetailKeyMessage>

      <Tabs
        variant="underline"
        className="global-insights-overlay__risk-detail-tabs"
        items={[
          {
            key: "action",
            label: "Action & impact",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Recommended action"
                  aiGenerated
                  itemKey="recommended-action-summary"
                >
                  <p className="global-insights-overlay__risk-detail-text">
                    {display(item.recommended_action?.what)}
                  </p>
                  <DetailSubsection label="Who">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(item.recommended_action?.who)}
                    </p>
                  </DetailSubsection>
                  <DetailSubsection label="When">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(item.recommended_action?.when)}
                    </p>
                  </DetailSubsection>
                  <DetailSubsection label="Escalation or decision required">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(item.recommended_action?.escalation_or_decision_required)}
                    </p>
                  </DetailSubsection>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Attention assessment"
                  aiGenerated
                  itemKey="attention-assessment"
                  statusBadge={
                    assessment === overviewRow.priority ? null : <DetailStatusBadge value={assessment} />
                  }
                >
                  {item.risk_basis != null && item.risk_basis.trim() !== "" && (
                    <DetailSubsection label="Assessment basis">
                      <p className="global-insights-overlay__risk-detail-text">{item.risk_basis}</p>
                    </DetailSubsection>
                  )}
                  <DetailSubsection label="Attention drivers">
                    <BulletList items={attentionDrivers} />
                  </DetailSubsection>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Delivery impact"
                  aiGenerated
                  itemKey="delivery-impact"
                >
                  <DetailSubsection label="Programme impact">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(item.programme_impact)}
                    </p>
                  </DetailSubsection>
                  <DetailSubsection label="Impact to my role">
                    <p className="global-insights-overlay__risk-detail-text">
                      {display(item.impact_to_my_role)}
                    </p>
                  </DetailSubsection>
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "plan",
            label: "Plan & evidence",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection heading="Plan dates" itemKey="plan-dates">
                  <ClientTable
                    className="global-insights-overlay__risk-detail-table global-insights-overlay__risk-detail-table--borderless"
                    data={scheduleRows}
                    rowKey={() => "planning-dates"}
                    columns={[
                      {
                        id: "baselineStart",
                        header: "Baseline start",
                        renderCell: (row) => displayDate(row.baselineStart),
                      },
                      {
                        id: "baselineEnd",
                        header: "Baseline end",
                        renderCell: (row) => displayDate(row.baselineEnd),
                      },
                      {
                        id: "forecastStart",
                        header: "Forecast start",
                        renderCell: (row) => displayDate(row.forecastStart),
                      },
                      {
                        id: "forecastEnd",
                        header: "Forecast end",
                        renderCell: (row) => displayDate(row.forecastEnd),
                      },
                      {
                        id: "actualStart",
                        header: "Actual start",
                        renderCell: (row) => displayDate(row.actualStart),
                      },
                      {
                        id: "actualEnd",
                        header: "Actual end",
                        renderCell: (row) => displayDate(row.actualEnd),
                      },
                    ]}
                  />
                </DetailAccordionSection>
                {sourceContext.length > 0 && (
                  <DetailAccordionSection
                    heading="Recorded plan context"
                    itemKey="recorded-plan-context"
                  >
                    <BulletList items={sourceContext} />
                  </DetailAccordionSection>
                )}

                <DetailAccordionSection heading="Evidence" aiGenerated itemKey="evidence">
                  <DetailSubsection label="Primary planning evidence">
                    <PlanningEvidenceList items={item.primary_planning_evidence} />
                  </DetailSubsection>
                  <DetailSubsection label="Supporting evidence">
                    <PlanningEvidenceList items={item.supporting_evidence} />
                  </DetailSubsection>
                </DetailAccordionSection>

                {supportingItems.length > 0 && (
                  <DetailAccordionSection
                    heading="Supporting linked items"
                    aiGenerated
                    itemKey="supporting-linked-items"
                  >
                    <LinkedItems
                      items={supportingItems.map((linked) => ({
                        linked_item_id: linked.item_id,
                        linked_item_type: linked.item_type,
                        evidence: linked.safe_description,
                      }))}
                    />
                  </DetailAccordionSection>
                )}
              </DetailAccordionGroup>
            ),
          },
        ]}
      />
    </DetailModalShell>
  );
}
